import {
  DataStatus,
  DropdownGroup,
  DropdownItem,
  FormControl,
  Label,
  LabelActions,
  openDropdown,
  showNotification,
} from 'platform/components';
import {HStack, Link, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useState} from 'react';
import {UseFormReturn, useWatch} from 'react-hook-form';

import {always, isNil, isNotNil} from 'ramda';

import {
  ContextTemplateResponseBody,
  TemplateContextEnum,
  useGetContextTemplateListQuery,
  useRenderBusinessCaseEmailTemplateMutation,
  useRenderCustomerEmailTemplateMutation,
  useRenderInterestEmailTemplateMutation,
  useRenderRentalOrderEmailTemplateMutation,
  useRenderVehicleEmailTemplateMutation,
  useRenderWorkshopInterestEmailTemplateMutation,
} from '@dms/api/core';
import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {myProfileRoutes} from '@dms/routes';

import {Nullish} from 'shared/web';

import {useEmailBodyImageUpload} from '../../../hooks/useEmailBodyImageUpload';
import {useTemplatesByLocale} from '../../../hooks/useTemplatesByLocale';
import {useTenant} from '../../../hooks/useTenant';
import {handleApiError} from '../../../utils/handleApiError';
import {replaceVariables, VARIABLE_REGEX} from '../../../utils/replaceVariables';
import {templateBodyToEmailHtml} from '../../../utils/templateBodyToEmailHtml';
import {AiComposerEditor} from '../../AiMessageComposer/AiComposerDraft';
import {AiMessageComposerPanel} from '../../AiMessageComposer/AiMessageComposerPanel';
import {AiComposerAppliedStatus} from '../../AiMessageComposer/components/AiComposerAppliedStatus';
import {AiComposerSelectionActions} from '../../AiMessageComposer/components/AiComposerSelectionActions';
import {getAiComposerContext} from '../../AiMessageComposer/getAiComposerContext';
import {getAiComposerEditorHighlights} from '../../AiMessageComposer/getAiComposerEditorHighlights';
import {useAiMessageComposer} from '../../AiMessageComposer/useAiMessageComposer';
import {EMAIL_DIALOG_BODY_MIN_ROWS} from '../../EmailBodyFormField/EMAIL_DIALOG_BODY_MIN_ROWS';
import {EmailBodyFormField} from '../../EmailBodyFormField/EmailBodyFormField';
import {ReplyToMessage} from '../types/ReplyToMessage';
import {FormValues} from '../types/SendCustomerEmailFormSchema';

interface MessageFormFieldProps {
  isBasicMessagingOnly?: boolean;
  formApi: UseFormReturn<FormValues>;
  control: FormControl<FormValues>;
  resourceId: EntityResourceIds | Nullish;
  recordId: string | Nullish;
  preferredLocale?: string | Nullish;
  upload: ReturnType<typeof useEmailBodyImageUpload>;
  replyTo?: ReplyToMessage | Nullish;
  isAiComposerInitiallyOpen?: boolean;
}

export function MessageFormField(props: MessageFormFieldProps) {
  if (props.isBasicMessagingOnly || props.resourceId === EntityResourceIds.directSale) {
    return (
      <EmailBodyFormField
        control={props.control}
        name="body"
        upload={props.upload}
        minRows={EMAIL_DIALOG_BODY_MIN_ROWS}
        isRequired
        data-testid="sendCustomerEmail"
      />
    );
  }
  return <AdvancedMessageFormField {...props} />;
}

function AdvancedMessageFormField(props: MessageFormFieldProps) {
  const {tenant} = useTenant();

  const context = match<EntityResourceIds | Nullish, TemplateContextEnum | null>(props.resourceId)
    .with(EntityResourceIds.businessCase, always('business-case'))
    .with(EntityResourceIds.customer, always('customer'))
    .with(EntityResourceIds.serviceOrder, always('service-order'))
    .with(EntityResourceIds.rentalOrder, always('rental-order'))
    .with(EntityResourceIds.vehicle, always('vehicle'))
    .with(EntityResourceIds.interest, always('interest'))
    .with(EntityResourceIds.workshopInterest, always('workshop-interest'))
    .otherwise(always(null));

  const [renderCustomerTemplate, {isLoading: isRenderCustomerTemplateInProgress}] =
    useRenderCustomerEmailTemplateMutation();
  const [renderBusinessCaseTemplate, {isLoading: isRenderBusinessCaseTemplateInProgress}] =
    useRenderBusinessCaseEmailTemplateMutation();
  const [renderRentalOrderTemplate, {isLoading: isRenderRentalOrderTemplateInProgress}] =
    useRenderRentalOrderEmailTemplateMutation();
  const [renderVehicleTemplate, {isLoading: isRenderVehicleTemplateInProgress}] =
    useRenderVehicleEmailTemplateMutation();
  const [renderInterestTemplate, {isLoading: isRenderInterestTemplateInProgress}] =
    useRenderInterestEmailTemplateMutation();
  const [renderWorkshopInterestTemplate, {isLoading: isRenderWorkshopInterestTemplateInProgress}] =
    useRenderWorkshopInterestEmailTemplateMutation();

  const isRenderTemplateInProgress =
    isRenderCustomerTemplateInProgress ||
    isRenderBusinessCaseTemplateInProgress ||
    isRenderRentalOrderTemplateInProgress ||
    isRenderVehicleTemplateInProgress ||
    isRenderInterestTemplateInProgress ||
    isRenderWorkshopInterestTemplateInProgress;

  const hasContext = isNotNil(context);

  const {
    data: templates,
    isLoading: isLoadingTemplates,
    isError: isTemplatesError,
  } = useGetContextTemplateListQuery(
    {
      type: 'email',
      context: context ?? 'customer',
    },
    {skip: !hasContext}
  );

  const getRenderer = (template: ContextTemplateResponseBody) => {
    if (isNil(props.resourceId) || isNil(props.recordId)) {
      return;
    }

    if (props.resourceId === EntityResourceIds.rentalOrder) {
      return renderRentalOrderTemplate({
        body: {
          rentalOrderId: props.recordId,
          template: {
            subject: template.subject ?? '',
            body: template.body,
          },
        },
      });
    }

    if (props.resourceId === EntityResourceIds.vehicle) {
      return renderVehicleTemplate({
        body: {
          vehicleId: props.recordId,
          template: {
            subject: template.subject ?? '',
            body: template.body,
          },
        },
      });
    }

    if (props.resourceId === EntityResourceIds.businessCase) {
      return renderBusinessCaseTemplate({
        renderBusinessCaseEmailTemplateRequestBody: {
          template: {
            subject: template.subject ?? '',
            body: template.body,
          },
          businessCaseId: props.recordId,
        },
      });
    }

    if (props.resourceId === EntityResourceIds.interest) {
      const locale = template.locale ?? tenant?.defaultLocale;
      if (isNil(locale)) {
        showNotification.error(i18n.t('general.notifications.dataStillLoadingTryAgain'));
        return;
      }
      return renderInterestTemplate({
        body: {
          subject: template.subject ?? '',
          body: template.body,
          interestId: props.recordId,
          locale,
        },
      });
    }

    if (props.resourceId === EntityResourceIds.workshopInterest) {
      const locale = template.locale ?? tenant?.defaultLocale;
      if (isNil(locale)) {
        showNotification.error(i18n.t('general.notifications.dataStillLoadingTryAgain'));
        return;
      }
      return renderWorkshopInterestTemplate({
        body: {
          subject: template.subject ?? '',
          body: template.body,
          workshopInterestId: props.recordId,
          locale,
        },
      });
    }

    if (props.resourceId === EntityResourceIds.customer) {
      return renderCustomerTemplate({
        renderCustomerEmailTemplateRequestBody: {
          template: {
            subject: template.subject ?? '',
            body: template.body,
          },
          customerId: props.recordId,
        },
      });
    }
  };

  const onTemplateClick = (template: ContextTemplateResponseBody) => {
    const renderer = getRenderer(template);

    if (!renderer) {
      return;
    }

    return renderer
      .unwrap()
      .then((response) => {
        props.formApi.setValue('subject', replaceVariables(response.subject));
        props.formApi.setValue('body', templateBodyToEmailHtml(response.body));
        if (VARIABLE_REGEX.test(response.subject) || VARIABLE_REGEX.test(response.body)) {
          showNotification.warning(i18n.t('general.notifications.notAllPlaceholdersFilled'));
        }
      })
      .catch(handleApiError);
  };

  const templatesByLocale = useTemplatesByLocale(templates, props.preferredLocale);

  const [body, subject, selectedCustomerId, recipientAddress] = useWatch({
    control: props.control,
    name: ['body', 'subject', 'customerId', 'customerEmailAddress'],
  });

  const [editor, setEditor] = useState<AiComposerEditor>(null);

  // Contact-card emails have a recipient but no enclosing record.
  const sourceResourceId = props.resourceId ?? EntityResourceIds.customer;
  const sourceRecordId = isNil(props.resourceId) ? selectedCustomerId : props.recordId;

  const composer = useAiMessageComposer({
    channel: 'email',
    bodyFormat: 'HTML',
    draft: {body: body ?? '', subject: subject ?? undefined},
    record: {
      context: getAiComposerContext(sourceResourceId),
      recordId: sourceRecordId,
      customerId: selectedCustomerId,
      recipientAddress,
      replyTo: props.replyTo,
    },
    editor,
    locale: props.preferredLocale,
    isInitiallyOpen: props.isAiComposerInitiallyOpen ?? isNotNil(props.replyTo),
    onApply: (draft) => {
      props.formApi.setValue('body', draft.body);
      if (isNotNil(draft.subject)) {
        props.formApi.setValue('subject', draft.subject);
      }
    },
  });

  const templateAction: LabelActions[number] = {
    title: i18n.t('entity.customer.labels.chooseEmailTemplate'),
    leftIcon: 'action/autorenew',
    isDisabled: isRenderTemplateInProgress,
    onClick: (event) =>
      openDropdown(
        event,
        <>
          <DataStatus isError={isTemplatesError} isLoading={isLoadingTemplates} />
          {templatesByLocale.isGrouped
            ? templatesByLocale.groups.map((group) => (
                <DropdownGroup key={group.key} label={group.label}>
                  {group.templates.map((template) => (
                    <DropdownItem
                      label={template.name}
                      key={template.id}
                      onClick={() => onTemplateClick(template)}
                    />
                  ))}
                </DropdownGroup>
              ))
            : templatesByLocale.templates.map((template) => (
                <DropdownItem
                  label={template.name}
                  key={template.id}
                  onClick={() => onTemplateClick(template)}
                />
              ))}
        </>,
        {
          isHeightLimited: true,
        }
      ),
  };

  const labelActions: LabelActions = [
    ...(isNotNil(composer.labelAction) ? [composer.labelAction] : []),
    ...(hasContext ? [templateAction] : []),
  ];

  return (
    <VStack spacing={1}>
      <EmailBodyFormField
        control={props.control}
        name="body"
        labelActions={labelActions}
        upload={props.upload}
        onEditorReady={setEditor}
        selectionContent={
          composer.isEnabled ? (
            <AiComposerSelectionActions controller={composer} data-testid="sendCustomerEmail" />
          ) : undefined
        }
        editorHighlights={getAiComposerEditorHighlights(composer)}
        onKeyDownCapture={composer.onEditorKeyDownCapture}
        minRows={EMAIL_DIALOG_BODY_MIN_ROWS}
        footerContent={
          <AiComposerAppliedStatus controller={composer} data-testid="sendCustomerEmail" />
        }
        editorContent={
          <AiMessageComposerPanel controller={composer} data-testid="sendCustomerEmail" />
        }
        isRequired
        data-testid="sendCustomerEmail"
      />
      <HStack justify="space-between" width="100%">
        <Label>{i18n.t('entity.customer.labels.messageWithSignature')}</Label>
        <Link
          leftIcon="image/edit"
          size="xSmall"
          href={myProfileRoutes.emailSignature}
          target="_blank"
        >
          {i18n.t('entity.customer.labels.goToUserSignature')}
        </Link>
      </HStack>
    </VStack>
  );
}
